/* eslint-disable @typescript-eslint/no-explicit-any */
import type { ProductIdentifier, ApiResponse, Options, WithOriginal, WithOriginalAndMapping } from './type';
import { getSolarRecommand } from '@/service/solar-calculator';

const isEmptyData = (data: ApiResponse) => typeof data === 'object' && data !== null && Object.keys(data).length === 0;

async function getProductInfo<T extends ProductIdentifier & Record<string, any>, O extends Options>(
    productInfo: T[],
    locale: string,
    options: O,
    keepOriginalData: true,
    isNeedEmptyData?: boolean,
): Promise<WithOriginalAndMapping<T, O>[]>;

async function getProductInfo<T extends ProductIdentifier & Record<string, any>, O extends Options>(
    productInfo: T[],
    locale: string,
    options: O,
    keepOriginalData: false,
    isNeedEmptyData?: boolean,
): Promise<WithOriginal<T, O>[]>;

async function getProductInfo<T extends ProductIdentifier & Record<string, any>, O extends Options>(
    productInfo: T[],
    locale: string,
    options?: O,
    keepOriginalData?: boolean,
    isNeedEmptyData?: boolean,
): Promise<WithOriginal<T, O>[]>;

async function getProductInfo<T extends ProductIdentifier & Record<string, any>, O extends Options>(
    productInfo: T[],
    locale: string,
    options?: O,
    keepOriginalData = false,
    isNeedEmptyData = false,
): Promise<WithOriginal<T, O>[] | WithOriginalAndMapping<T, O>[]> {
    const getAsynItem = async (productId: string, variantId: string): Promise<ApiResponse> => {
        if (!productId || !variantId) return {} as ApiResponse;
        if (productId === 'undefined' || variantId === 'undefined') return {} as ApiResponse;
        const res = await getSolarRecommand({ productId, variantId }, locale);
        if (res?.code === '0') return res.data as ApiResponse;
        throw new Error(res?.message || 'Failed to fetch');
    };

    const enrichItem = async (item: T): Promise<(WithOriginal<T, O> | WithOriginalAndMapping<T, O>) | null> => {
        try {
            const data = await getAsynItem(String(item.productId), String(item.variantId));
            if (isNeedEmptyData && isEmptyData(data)) {
                return null;
            }
            const enriched: any = { ...item };

            (Object.keys(data) as (keyof ApiResponse)[]).forEach((key) => {
                const targetKey = options?.[key] || key;
                if (keepOriginalData) enriched[key] = data[key];
                // 没有拿到值就不赋值
                if (data[key] !== null && data[key] !== undefined) {
                    enriched[targetKey] = data[key];
                }
            });

            if (Array.isArray(item.children)) {
                const children = await Promise.all(item.children.map(enrichItem as any));
                enriched.children = isNeedEmptyData ? children.filter(Boolean) : children;
            }

            return enriched;
        } catch (e) {
            let children;
            if (item.children) {
                const childResults = await Promise.all(item.children.map(enrichItem as any));
                children = isNeedEmptyData ? childResults.filter(Boolean) : childResults;
            }
            return {
                ...item,
                children,
            } as WithOriginal<T, O>;
        }
    };

    return Promise.all(productInfo.map(enrichItem)).then((results) => {
        return isNeedEmptyData
            ? (results.filter(Boolean) as WithOriginal<T, O>[] | WithOriginalAndMapping<T, O>[])
            : (results as WithOriginal<T, O>[] | WithOriginalAndMapping<T, O>[]);
    });
}

export default getProductInfo;
